import { useEffect, useRef, useState } from "react";
import { api, eur, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Upload, Check, Trash2, AlertTriangle, Loader2 } from "lucide-react";

const nlDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; } catch { return iso; } };

function ReconcileRow({ item, onChange, onConfirm, onDelete, canWrite }) {
  const [date, setDate] = useState(item.date || "");
  const [amount, setAmount] = useState(item.bank_amount ?? "");
  const [busy, setBusy] = useState(false);

  const patch = async (payload) => {
    try {
      const r = await api.patch(`/kasboek/bank/${item.id}`, payload);
      onChange(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const confirm = async () => {
    if (!date) return toast.error("Vul eerst de datum in");
    setBusy(true);
    try {
      const r = await api.post(`/kasboek/bank/${item.id}/confirm`);
      toast.success(`Kasboeklijn geboekt: cash ${eur(r.data.cash)}`);
      onConfirm(item.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid={`bank-row-${item.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <CreditCard className="h-4 w-4 text-sky-600" />
          <span className="font-medium">{item.filename || "kaartlezer.jpg"}</span>
          {item.duplicate && <Badge variant="outline" className="text-red-600 border-red-300">Mogelijk dubbel</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <div className="space-y-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Datum</label>
            <Input type="date" className="h-8 w-40" data-testid={`bank-date-${item.id}`} value={date}
              onChange={(e) => setDate(e.target.value)} onBlur={() => date !== item.date && patch({ date })} />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Bank (kaartlezer)</label>
            <Input type="number" step="0.01" className="h-8 w-32 text-right" data-testid={`bank-amount-${item.id}`} value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => parseFloat(amount) !== item.bank_amount && patch({ bank_amount: parseFloat(amount) || 0 })} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-md bg-muted/50 py-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Totale omzet</div>
          <div className="font-heading font-semibold tabular-nums" data-testid={`bank-omzet-${item.id}`}>{eur(item.omzet)}</div>
        </div>
        <div className="rounded-md bg-sky-50 py-2">
          <div className="text-[11px] uppercase tracking-wide text-sky-700">Bank</div>
          <div className="font-heading font-semibold tabular-nums text-sky-700">{eur(item.bank)}</div>
        </div>
        <div className={`rounded-md py-2 ${item.cash_negative ? "bg-red-50" : "bg-emerald-50"}`}>
          <div className={`text-[11px] uppercase tracking-wide ${item.cash_negative ? "text-red-700" : "text-emerald-700"}`}>Cash ontvangsten</div>
          <div className={`font-heading font-semibold tabular-nums ${item.cash_negative ? "text-red-700" : "text-emerald-700"}`} data-testid={`bank-cash-${item.id}`}>{eur(item.cash)}</div>
        </div>
      </div>

      {item.cash_negative && (
        <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          <AlertTriangle className="h-4 w-4 shrink-0" /> Bank is hoger dan de omzet — controleer de datum/bedrag. Bevestigen boekt dit als uitgave.
        </div>
      )}
      {item.duplicate && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
          <AlertTriangle className="h-4 w-4 shrink-0" /> Voor {nlDate(item.date)} is al een kasafstemming bevestigd.
        </div>
      )}
      {item.date && !item.shops_complete && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2" data-testid={`bank-incomplete-${item.id}`}>
          <AlertTriangle className="h-4 w-4 shrink-0" /> Dagontvangsten nog niet voor alle winkels ingegeven op {nlDate(item.date)}
          {item.missing_shops?.length ? <span> — ontbreekt: <b>{item.missing_shops.join(", ")}</b></span> : null}. Bevestigen kan pas als alle winkels ingevuld zijn.
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" className="text-red-500" data-testid={`bank-delete-${item.id}`} onClick={() => onDelete(item.id)}>
          <Trash2 className="h-4 w-4 mr-1" /> Verwijderen
        </Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !canWrite || !date || !item.shops_complete} data-testid={`bank-confirm-${item.id}`} onClick={confirm}>
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />} Bevestigen
        </Button>
      </div>
    </div>
  );
}

export default function BankReconcile({ onConfirmed }) {
  const { canWrite } = useAuth();
  const fileRef = useRef();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/kasboek/bank/pending").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/kasboek/bank/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => [...r.data.items, ...prev]);
      toast.success(`${r.data.items.length} kaartlezer-foto('s) ingelezen — controleer datum & bedrag`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload mislukt"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const updateItem = (updated) => setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const doDelete = async (id) => {
    try { await api.delete(`/kasboek/bank/${id}`); removeItem(id); toast.success("Verwijderd"); }
    catch (e) { toast.error("Verwijderen mislukt"); }
  };
  const doConfirmed = (id) => { removeItem(id); onConfirmed?.(); };

  return (
    <Card className="shadow-sm border-sky-200" data-testid="bank-reconcile-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-sky-600" /> Kasafstemming (kaartlezer → cash)
        </CardTitle>
        <div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={upload} data-testid="bank-file-input" />
          <Button variant="outline" size="sm" disabled={uploading || !canWrite} data-testid="bank-upload-btn" onClick={() => fileRef.current?.click()}>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Kaartlezer-foto's uploaden
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Stuur of upload de kaartlezer-schermafbeelding. Het systeem leest de datum en het bankbedrag uit, berekent
          <b> cash = omzet (dagontvangsten 3 winkels) − bank</b> en stelt een kasboeklijn voor. Bevestig en de foto wordt verwijderd.
        </p>
        {items.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-6" data-testid="bank-empty">
            Geen openstaande kaartlezer-foto's. Upload er één om te beginnen.
          </div>
        ) : items.map((it) => (
          <ReconcileRow key={it.id} item={it} canWrite={canWrite}
            onChange={updateItem} onConfirm={doConfirmed} onDelete={doDelete} />
        ))}
      </CardContent>
    </Card>
  );
}
